import { createFileRoute } from '@tanstack/react-router'

import { LegalPage } from '@/features/legal/components/legal-page'

export const Route = createFileRoute('/terms')({
  component: TermsPage,
})

function TermsPage() {
  return (
    <LegalPage title="Terms of Service" lastUpdated="August 1, 2026">
      <section>
        <h2>1. Acceptance of Terms</h2>
        <p>
          These Terms of Service ("Terms") form a binding agreement between you
          and Melodex ("we," "us," or "our") governing your use of the Melodex
          platform at <a href="https://melodex.app">melodex.app</a> and any
          related services (together, the "Service"). By creating an account or
          using the Service, you agree to these Terms and to our{' '}
          <a href="/privacy">Privacy Policy</a>.
        </p>
        <p>If you do not agree, do not use the Service.</p>
      </section>

      <section>
        <h2>2. Eligibility and Accounts</h2>
        <p>
          You must be at least thirteen years old to use the Service, or at
          least sixteen if you reside in the European Economic Area. By using
          the Service you represent that you meet these age requirements and
          have the legal capacity to enter into these Terms.
        </p>
        <p>
          You are responsible for keeping your account credentials secure and
          for all activity that occurs under your account. Notify us immediately
          at <a href="mailto:apaterogroup@gmail.com">apaterogroup@gmail.com</a>{' '}
          if you suspect any unauthorized use.
        </p>
      </section>

      <section>
        <h2>3. Credits and Payment</h2>
        <p>
          Melodex uses a GP-based billing system. GP is consumed as you generate
          songs, scene images, and videos. You may purchase GP through the
          Service. All purchases are final and non-refundable except where
          required by applicable consumer protection law.
        </p>
        <p>
          GP has no cash value and cannot be transferred between accounts.
          Subscription GP does not roll over and expires when a new allowance is
          granted. GP purchased through a one-time pack does not expire on the
          subscription renewal schedule. Prices may change; we will not change
          the price of GP you have already purchased.
        </p>
      </section>

      <section>
        <h2>4. Acceptable Use</h2>
        <p>
          You agree not to use the Service, and not to cause or encourage any
          third party to use the Service, to create, upload, store, or
          distribute content that:
        </p>
        <ul>
          <li>Violates any law or regulation</li>
          <li>
            Infringes any copyright, trademark, or other intellectual property
            right
          </li>
          <li>
            Depicts or involves the sexual exploitation or abuse of minors
          </li>
          <li>
            Clones the voice of a real person without their explicit consent
          </li>
          <li>
            Creates sexual, defamatory, or harassing content depicting an
            identifiable real person without their consent
          </li>
          <li>Promotes violence, terrorism, self-harm, or hate speech</li>
          <li>
            Attempts to deceive or impersonate another person for fraudulent
            purposes
          </li>
          <li>Contains malware, phishing attempts, or other harmful code</li>
        </ul>
        <p>You also agree not to:</p>
        <ul>
          <li>
            Reverse engineer, decompile, or attempt to extract the source code
            of the Service
          </li>
          <li>
            Interfere with or disrupt the Service, including rate-limiting
            systems, through automated traffic or scraping
          </li>
          <li>Use the Service to train a competing generative model</li>
          <li>
            Resell, sublicense, or repackage the Service without our written
            permission
          </li>
        </ul>
        <p>
          We may suspend or terminate accounts that violate these rules, and in
          serious cases may report them to the appropriate authorities.
        </p>
      </section>

      <section>
        <h2>5. Your Content</h2>
        <p>
          You retain ownership of the content you upload to the Service
          ("Input") and of the outputs that the Service generates for you based
          on your Input ("Output"). You grant us a limited, worldwide,
          royalty-free license to host, process, reproduce, and display your
          Input and Output for the sole purpose of operating and improving the
          Service on your behalf.
        </p>
        <p>
          You represent that you have all rights necessary to submit your Input
          and to grant us this license. You are solely responsible for your
          Input and for how you use your Output.
        </p>
      </section>

      <section>
        <h2>6. AI-Generated Output</h2>
        <p>
          Melodex relies on machine learning models. Output is generated
          probabilistically and we cannot guarantee that Output will be unique,
          accurate, fit for a particular purpose, or free from content that
          resembles copyrighted or trademarked material. Before publishing or
          commercially exploiting Output, you are responsible for reviewing it
          and confirming that it complies with applicable laws and with
          third-party rights.
        </p>
        <p>
          Because similar prompts can produce similar Output for different
          users, we do not warrant exclusivity over any generated result.
        </p>
      </section>

      <section>
        <h2>7. Our Intellectual Property</h2>
        <p>
          The Service itself, including its source code, interface, branding,
          and documentation, is owned by us or our licensors and is protected by
          copyright, trademark, and other intellectual property laws. These
          Terms grant you no rights in our intellectual property except the
          limited right to use the Service in accordance with these Terms.
        </p>
      </section>

      <section>
        <h2>8. Third-Party Services</h2>
        <p>
          The Service uses third-party providers (listed in our{' '}
          <a href="/privacy">Privacy Policy</a>) to deliver certain features.
          Availability, pricing, and behavior of those features may change if a
          provider changes its offering. We are not responsible for third-party
          services beyond what our agreements with those providers cover.
        </p>
      </section>

      <section>
        <h2>9. Suspension and Termination</h2>
        <p>
          You may request account closure using the contact link on the Settings
          page or by emailing us. We may suspend or terminate your account if
          you breach these Terms, if required by law, or if your use of the
          Service poses a security or fraud risk. Upon termination, your right
          to use the Service ends immediately. Sections of these Terms that by
          their nature should survive termination (for example, sections on
          intellectual property, disclaimers, limitations of liability, and
          governing law) will continue to apply.
        </p>
      </section>

      <section>
        <h2>10. Disclaimers</h2>
        <p>
          The Service is provided "as is" and "as available," without warranties
          of any kind, whether express or implied, including without limitation
          any implied warranties of merchantability, fitness for a particular
          purpose, or non-infringement. We do not warrant that the Service will
          be uninterrupted, error-free, or secure, or that Output will meet your
          expectations.
        </p>
      </section>

      <section>
        <h2>11. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, Melodex, its owners, and its
          affiliates will not be liable for any indirect, incidental, special,
          consequential, or punitive damages, or for any loss of profits,
          revenue, data, goodwill, or business opportunity, arising out of or in
          connection with your use of the Service, even if we have been advised
          of the possibility of such damages.
        </p>
        <p>
          Our total liability for any claim arising out of or relating to the
          Service is limited to the amount you paid us for the Service in the
          twelve months preceding the event giving rise to the claim, or one
          hundred euros, whichever is greater.
        </p>
        <p>
          Some jurisdictions do not allow the exclusion or limitation of certain
          damages, so the limits above may not apply to you.
        </p>
      </section>

      <section>
        <h2>12. Indemnification</h2>
        <p>
          You agree to defend, indemnify, and hold harmless Melodex and its
          affiliates from any claim, demand, loss, or damage arising out of (a)
          your Input or Output, (b) your use of the Service in violation of
          these Terms, or (c) your violation of any law or third-party right.
        </p>
      </section>

      <section>
        <h2>13. Governing Law</h2>
        <p>
          These Terms are governed by the laws of the Republic of Albania,
          without regard to conflict of law principles. You and we agree that
          the courts located in Tirana, Albania have exclusive jurisdiction over
          any dispute arising out of or relating to these Terms or the Service,
          except that we may seek injunctive relief in any court of competent
          jurisdiction to protect our intellectual property.
        </p>
      </section>

      <section>
        <h2>14. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. If we make material
          changes we will notify you by email or by showing a notice inside the
          Service before the changes take effect. Your continued use of the
          Service after changes become effective constitutes your acceptance of
          the updated Terms.
        </p>
      </section>

      <section>
        <h2>15. Contact</h2>
        <p>
          Questions about these Terms? Email{' '}
          <a href="mailto:apaterogroup@gmail.com">apaterogroup@gmail.com</a>.
        </p>
      </section>
    </LegalPage>
  )
}
